/**
 * Laying out a chase binder's grid.
 *
 * A chase binder gets the master set's slot-grid treatment - owned bright,
 * missing faded, a missing-only filter - and none of the master set's slot
 * MODEL. Where a master set's slots are the catalogue crossed with its
 * printings, a chase binder's slots are simply the rows the person put in it,
 * from `public.wish_binder_cards`. So the denominator is the binder's own size
 * and there is no printing axis to badge: nobody adds "Base Set Charizard,
 * shadowless" to a chase binder, they add Charizard.
 *
 * That is why nothing here goes near `wish_set_slot_grid`. Forcing binder rows
 * through it would invent printings the person never chose and count slots they
 * never asked for.
 *
 * WHERE THE ORDER COMES FROM.
 *
 * `cards` is the catalogue's answer to the binder's ids under the current sort
 * and filters, read in one request through `listCatalogCards({ ids })`. So the
 * order and the filtering are the database's, exactly as they are in Explore and
 * in the master set grid, rather than a second sort implementation living in the
 * client.
 *
 * A card the catalogue has no row for is still drawn, from what the binder row
 * itself stored, and it sorts to the end. A card cannot silently disappear from
 * a list somebody built by hand - but it also cannot jump the order, because the
 * database never ranked it.
 */

/**
 * @param {{
 *   rows: object[], cards: object[], owned?: Set<string>|null,
 *   ownership?: boolean, missingOnly?: boolean, filtered?: boolean,
 * }} input
 *   rows      the binder's own `wish_binder_cards` rows
 *   cards     catalogue rows for those ids, already sorted and filtered
 *   owned     the acting profile's card ids, from fetchOwnedCardIds
 *   ownership false draws every tile bright, which is what somebody else's
 *             binder gets: their progress is not ours to fade
 *   filtered  true when a catalogue filter is narrowing the list, which is the
 *             one case where a card with no catalogue row must be dropped
 *             rather than appended - it was never tested against the filter
 */
export function buildBinderTiles({
  rows = [], cards = [], owned = null, ownership = true, missingOnly = false, filtered = false,
}) {
  const byId = new Map();
  for (const row of rows) if (row?.tcgdex_card_id) byId.set(row.tcgdex_card_id, row);

  const tiles = [];
  const drawn = new Set();

  for (const card of cards) {
    const row = byId.get(card.id);
    if (!row) continue;
    drawn.add(card.id);
    const tile = binderTile(row, card, owned, ownership);
    if (missingOnly && tile.owned) continue;
    tiles.push(tile);
  }

  if (!filtered) {
    for (const row of rows) {
      const id = row?.tcgdex_card_id;
      if (!id || drawn.has(id)) continue;
      drawn.add(id);
      const tile = binderTile(row, null, owned, ownership);
      if (missingOnly && tile.owned) continue;
      tiles.push(tile);
    }
  }

  return tiles;
}

/**
 * How full the binder is.
 *
 * Counted over the binder's own ROWS rather than over the tiles on screen, so
 * turning on Missing only or a type filter narrows what is drawn and never
 * rewrites what the gauge claims. Filtering to Grass does not make a 50-card
 * binder a 9-card binder.
 */
export function binderProgress(rows = [], owned = null, ownership = true) {
  const total = rows.length;
  if (!ownership || !owned) return { total, filled: null };
  let filled = 0;
  for (const row of rows) if (row?.tcgdex_card_id && owned.has(row.tcgdex_card_id)) filled += 1;
  return { total, filled };
}

function binderTile(row, card, owned, ownership) {
  const id = row.tcgdex_card_id;
  return {
    // The binder row's own id, which is stable and unique inside the binder,
    // and is what a remove would act on.
    id: row.id || id,
    cardId: id,
    localId: (card?.local_id) || String(id || '').replace(`${row.set_id || ''}-`, ''),
    name: card?.name || row.card_name || 'Card',
    setId: card?.set_id || row.set_id || null,
    setName: card?.set_name || row.set_name || null,
    // The catalogue's art first. The stored copy is a fallback rather than the
    // source of truth, because art gets re-mirrored and a URL frozen into a
    // wish row at the moment somebody tapped Add can rot.
    imageUrl: card?.image_url || row.image_url || null,
    price: card?.near_mint ?? null,
    owned: ownership && !!owned && owned.has(id),
    // A chase binder has no printing axis, so there is nothing to badge and no
    // unrecorded-printing case to explain.
    badge: null,
    printingUnknown: false,
    row,
  };
}
